import React, { KeyboardEvent, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { SvgIconComponent } from "@mui/icons-material";

interface InlineEditProps {
    value: string;
    displayValue?: string;
    Icon?: SvgIconComponent;
    iconElement?: React.ReactNode;
    startEditing?: boolean;
    handleSave: (newValue: string) => Promise<void>;
    renderEdit: (props: {
        value: string;
        onChange: (value: string) => void;
        onBlur: () => void;
        onKeyDown: (e: KeyboardEvent) => void;
    }) => React.ReactNode;
    emptyText?: string;
    className?: string;
}

export function InlineEdit({
    value,
    displayValue,
    Icon,
    iconElement,
    startEditing = false,
    handleSave,
    renderEdit,
    emptyText = "",
    className = "",
}: InlineEditProps) {
    const [editing, setEditing] = useState<boolean>(!!startEditing);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (startEditing) setEditing(true);
    }, [startEditing]);

    const handleChange = async(newValue: string) => {
        setLoading(true);
        try {
            await handleSave(newValue);
            setEditing(false);
        } finally {
            setLoading(false);
        }
    };

    if (editing) {
        return (
            <>
                <LoadingIndicator loading={loading} />
                {renderEdit({
                    value,
                    onChange: (v) => {
                        void handleChange(v);
                    },
                    onBlur: () => setEditing(false),
                    onKeyDown: (e) => {
                        if (e.key === "Escape") {
                            setEditing(false);
                        }
                    },
                })}
            </>
        );
    }

    const shownText = !!value ? (displayValue ?? value) : emptyText;

    return (
        <Box
            className={`hover-effect ${className}`}
            sx={{ cursor: "pointer", display: "flex", alignItems: "center", maxWidth: "100%" }}
            onClick={(e) => {
                e.stopPropagation();
                setEditing(true);
            }}
        >
            {iconElement
                ? <span style={{ marginRight: 4, display: "inline-flex" }}>{iconElement}</span>
                : Icon && <Icon fontSize="small" color="primary" sx={{ mr: 0.5 }} />}

            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    fontSize="small"
                    component="span"
                    noWrap
                    sx={{ display: "block" }}
                    fontWeight={!!value ? "600" : undefined}
                    fontStyle={!!value ? undefined : "italic"}
                    color={!!value ? undefined : "text.secondary"}
                >
                    {shownText}
                </Typography>
            </Box>
        </Box>
    );
}
